import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { routeInspectionSheet } from "@/setup/routes/inspection.routes";
import { InspectionSectionSelector } from "@/shared/selectors/InspectionSectionSelector";
import {
	ErrorModal,
	HelmetDexteel,
	MesfModal,
	useToken,
} from "@dexteel/mesf-core";
import { Grid2, TextField } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { upsertInspection } from "../../repositories/InspectionRepository";

type Props = {
	modal: "create" | "";
	shiftId: number | null;
	onHide: () => void;
};

export const NewInspectionModal = ({ modal, shiftId, onHide }: Props) => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { getUserId } = useToken();

	const [sectionId, setSectionId] = useState<number | null>(null);
	const [templateId, setTemplateId] = useState<number | null>(null);
	const [comments, setComments] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [error, setError] = useState("");

	const createMutation = useMutation(upsertInspection, {
		onSuccess: async (inspectionId: number) => {
			await queryClient.invalidateQueries({ queryKey: ["inspections"] });
			await queryClient.invalidateQueries({
				queryKey: ["inspection-sections"],
			});
			setNotificationMessage("The inspection was created succesfully");
			onHide();
			navigate(`${routeInspectionSheet}/${inspectionId}`);
		},
		onError: (mutationError: Error) => setError(mutationError.message),
	});

	const onSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		if (templateId === null || shiftId === null) return;

		const userId = Number(getUserId());

		createMutation.mutate({
			templateId,
			shiftId,
			userId: userId > 0 ? userId : null,
			comments: comments === "" ? null : comments,
		});
	};

	useEffect(() => {
		if (modal === "") return;
		setSectionId(null);
		setTemplateId(null);
		setComments("");
	}, [modal]);

	return (
		<>
			{modal === "create" && (
				<HelmetDexteel title="New Inspection - Inspections Manager" />
			)}
			<MesfModal
				id="modal-new-inspection"
				title="NEW INSPECTION"
				open={modal !== ""}
				handleClose={onHide}
				maxWidth="sm"
			>
				<form onSubmit={onSubmit}>
					<MesfModal.Content style={{ padding: "15px 30px" }} dividers>
						<Grid2 container spacing={2}>
							<Grid2 size={{ xs: 12 }}>
								<InspectionSectionSelector
									value={sectionId}
									shiftId={shiftId}
									required
									onChange={(value, option) => {
										setSectionId(value);
										setTemplateId(option?.TemplateId ?? null);
									}}
									onError={(sectionsError) => setError(sectionsError.message)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12 }}>
								<TextField
									label="Comments"
									value={comments}
									onChange={(event) => setComments(event.target.value)}
									fullWidth
									multiline
									minRows={2}
									size="small"
									autoComplete="off"
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<FooterModalButtons
							isSubmitLoading={createMutation.isLoading}
							onHide={onHide}
							buttonLabel="Create"
							buttonColor="primary"
							disabled={templateId === null || shiftId === null}
						/>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
